import { useCallback, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { bridge, isNotNil } from 'shared/src/utils';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import type { LikeGoodsRow } from '../LikeGoods';
import type { LikedGoodsCardItem } from '../utils/convertLikedGoodsItemToGoodsData';

interface UseLogDisplayGoodsParams {
  /** 카테고리/정렬/배송 필터 변경 등으로 리스트가 갱신되면 중복 방지 Set 초기화 */
  resetKey: unknown;
}

/**
 * onViewableItemsChanged 콜백.
 * `rows`는 row 단위(goods 1행에 최대 3개)이므로 viewable row가 들어오면
 * 그 안의 상품 각각에 대해 DISPLAY_GOODS / display ad 로깅 발화.
 * SCREEN 은 AnalyticsContext 에서 자동 주입.
 */
export const useLogDisplayGoods = ({ resetKey }: UseLogDisplayGoodsParams) => {
  const loggedSnoRef = useRef<Set<number>>(new Set());
  const logAnalyticsEvent = useLogAnalyticsEvent();

  useEffect(() => {
    loggedSnoRef.current = new Set();
  }, [resetKey]);

  const logEntry = useCallback(
    (entry: LikedGoodsCardItem) => {
      if (loggedSnoRef.current.has(entry.item.sno)) return;
      loggedSnoRef.current.add(entry.item.sno);

      logAnalyticsEvent({
        name: 'DISPLAY_GOODS',
        analytics: {
          ad: entry.item.ad,
          ...entry.logging.analytics,
        },
        ably_only_log: entry.logging.ably_only_log,
      });

      if (isNotNil(entry.item.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: entry.item.ad });
      }
    },
    [logAnalyticsEvent],
  );

  return useCallback<
    NonNullable<FlatListProps<LikeGoodsRow>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ item: row, isViewable }) => {
        if (!isViewable || row.type !== 'gridRow') return;
        row.slots.forEach(slot => {
          if (slot.type === 'goods') logEntry(slot.entry);
        });
      });
    },
    [logEntry],
  );
};
